import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Reveal } from '../Reveal';
import { SectionHeading, Toggle } from '../ui';
import { DURATION, EASE_OUT_EXPO } from '../../lib/motion';

/**
 * Privacy section.
 *
 * An interactive demo of the real privacy profile: flip the toggles and the
 * preview card next to them gains and loses fields exactly the way the server's
 * projection does. Nothing explains "настраиваемая приватность" as well as
 * letting someone watch their booking reference disappear.
 */
export function PrivacySection() {
  const [showDates, setShowDates] = useState(false);
  const [showTravelers, setShowTravelers] = useState(false);
  const [showLodging, setShowLodging] = useState(true);
  const [showPhotos, setShowPhotos] = useState(true);

  return (
    <section id="privacy" className="py-24 md:py-36">
      <div className="container-page">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <SectionHeading
              eyebrow="Приватность"
              title="Одно путешествие — два разных вида"
              description="Для вас — с билетами, PIN-кодами от апартаментов и суммами. Для остальных — маршрут и места. Что именно попадёт в публичную версию, решаете вы переключателями."
            />

            <div className="mt-8 max-w-md divide-y divide-[var(--border-subtle)]">
              <Toggle
                checked={showDates}
                onChange={setShowDates}
                label="Показывать точные даты"
                description="Выключено — гости видят «июнь · 11 ночей»"
              />
              <Toggle
                checked={showTravelers}
                onChange={setShowTravelers}
                label="Показывать участников"
                description="Имена попутчиков в публичной версии"
              />
              <Toggle
                checked={showLodging}
                onChange={setShowLodging}
                label="Показывать, где жили"
                description="Названия отелей и точки на карте — но не адрес и не бронь"
              />
              <Toggle
                checked={showPhotos}
                onChange={setShowPhotos}
                label="Показывать фотографии"
                description="И у каждого фото есть собственная видимость"
              />
            </div>

            <p className="mt-8 max-w-md rounded-xl bg-[var(--surface-sunken)] px-4 py-3 text-sm leading-relaxed text-[var(--text-muted)]">
              Документы, номера броней и PIN-коды не показываются никогда — для них в публичном
              формате данных просто нет поля.
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="relative">
              <div className="surface-card overflow-hidden p-0">
                <div className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--surface-sunken)] px-5 py-3">
                  <span className="text-sm font-medium">Публичная версия</span>
                  <span className="rounded-full bg-lagoon-500/15 px-2.5 py-1 text-xs text-lagoon-600 dark:text-lagoon-300">
                    так видят гости
                  </span>
                </div>

                <div className="space-y-4 p-6">
                  <div>
                    <div className="display text-3xl">Турция 2026</div>
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={showDates ? 'exact' : 'blurred'}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: DURATION.fast, ease: EASE_OUT_EXPO }}
                        className="mt-1.5 text-sm text-[var(--text-muted)]"
                      >
                        {showDates ? '2–13 июня 2026' : 'июнь 2026 · 11 ночей'}
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  <Row label="Маршрут" value="Олюдениз → Каш → Кемер → Калкан" always />
                  <Row label="Точек на карте" value="34" always />
                  <Row
                    label="Участники"
                    value={showTravelers ? 'Костя, Наташа' : '2 путешественника'}
                    always
                  />

                  <AnimatePresence initial={false}>
                    {showLodging ? (
                      <Collapsible key="lodging">
                        <Row label="Где жили" value="Sunmed Lodge · Korsanada · Caretta" />
                      </Collapsible>
                    ) : null}
                    {showPhotos ? (
                      <Collapsible key="photos">
                        <div className="pt-1">
                          <div className="mb-2 text-xs uppercase tracking-wider text-[var(--text-muted)]">
                            Фотографии
                          </div>
                          <div className="grid grid-cols-5 gap-1.5">
                            {[
                              ['/assets/mini-sea.png', 'Море у берега'],
                              ['/assets/mini-village.png', 'Деревня по пути'],
                              ['/assets/mini-balcony.png', 'Утро в поездке'],
                              ['/assets/mini-path.png', 'Тропа к морю'],
                              ['/assets/mini-kayak.png', 'Каяк в бухте'],
                            ].map(([src, alt]) => (
                              <img key={src} src={src} alt={alt} className="aspect-square rounded object-cover" />
                            ))}
                          </div>
                        </div>
                      </Collapsible>
                    ) : null}
                  </AnimatePresence>

                  <div className="space-y-2 rounded-xl border border-dashed border-[var(--border-subtle)] p-4">
                    {['Билеты и ваучеры', 'Номера броней и PIN-коды', 'Сколько всё стоило'].map(
                      (item) => (
                        <div
                          key={item}
                          className="flex items-center gap-2 text-sm text-[var(--text-muted)]"
                        >
                          <span className="text-xs">🔒</span>
                          <span className="line-through decoration-[var(--text-muted)]/40">
                            {item}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>

              <motion.div
                aria-hidden
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="pointer-events-none absolute -right-3 -top-4 hidden rounded-xl bg-ink-900 px-3 py-2 text-xs text-sand-100 shadow-lift sm:block"
              >
                🔗 доступно без регистрации
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value, always }: { label: string; value: string; always?: boolean }) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 border-b border-[var(--border-subtle)] pb-3 ${
        always ? '' : 'pt-1'
      }`}
    >
      <span className="text-xs uppercase tracking-wider text-[var(--text-muted)]">{label}</span>
      <span className="text-right text-sm font-medium">{value}</span>
    </div>
  );
}

function Collapsible({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: DURATION.fast, ease: EASE_OUT_EXPO }}
      className="overflow-hidden"
    >
      {children}
    </motion.div>
  );
}
